import type { StoredDocument } from "../db/types";
import type { Config } from "../state/config";
import { embedTexts } from "./rcpLLM";
import { authFetch } from "./authFetch";
import { i18n, tr } from "../i18n/i18n.svelte";

interface ApiChunk {
  id: string;
  text: string;
  page: number;
  section: string | null;
}

interface ParsedDocResponse {
  docId: string;
  name: string;
  hash: string;
  nChunks: number;
  parsedBy: string;
  ocrUsed: boolean;
  chunks: ApiChunk[];
}

function baseUrl(cfg: Pick<Config, "serverUrl">): string {
  return cfg.serverUrl.replace(/\/$/, "");
}

export class ServerError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ServerError";
  }
}

async function throwIfBad(res: Response): Promise<never> {
  let detail = "";
  try {
    const json = await res.json();
    detail = json.detail ?? JSON.stringify(json);
  } catch {
    detail = await res.text().catch(() => "");
  }
  throw new ServerError(res.status, `${tr({ fr: "Serveur", en: "Server" })} ${res.status}: ${detail}`);
}

/** Server-Sent Events emitted by /parse/stream. */
export type ParseProgress =
  | { type: "phase"; phase: "received"; total_pages: number; size_bytes: number }
  | { type: "phase"; phase: "native_extracting" }
  | { type: "phase"; phase: "native_extracted"; n_pages: number; avg_chars: number }
  | { type: "phase"; phase: "ocr_started"; total_pages: number }
  | { type: "phase"; phase: "chunking"; n_chunks?: number }
  | { type: "progress"; step: "ocr"; page: number; total: number }
  | { type: "result"; data: ParsedDocResponse }
  | { type: "error"; detail: string };

/** Human-readable label (interface language) for a progress event — what the UI shows. */
export function progressLabel(p: ParseProgress): string {
  const en = i18n.locale === "en";
  if (p.type === "phase") {
    switch (p.phase) {
      case "received":
        return en
          ? `receiving (${p.total_pages} page${p.total_pages > 1 ? "s" : ""})…`
          : `réception (${p.total_pages} page${p.total_pages > 1 ? "s" : ""})…`;
      case "native_extracting":
        return en ? "extracting text…" : "extraction du texte…";
      case "native_extracted":
        return en
          ? `text extracted (${p.n_pages} page${p.n_pages > 1 ? "s" : ""}, ${p.avg_chars} chars/page on average)`
          : `texte extrait (${p.n_pages} page${p.n_pages > 1 ? "s" : ""}, ${p.avg_chars} car./page en moyenne)`;
      case "ocr_started":
        return en
          ? `OCR started (${p.total_pages} page${p.total_pages > 1 ? "s" : ""})…`
          : `OCR démarré (${p.total_pages} page${p.total_pages > 1 ? "s" : ""})…`;
      case "chunking":
        return en ? "splitting into chunks…" : "découpage en chunks…";
    }
  }
  if (p.type === "progress" && p.step === "ocr") {
    return `OCR page ${p.page}/${p.total}…`;
  }
  return "";
}

/** Parse the file server-side via SSE, then embed its chunks client-side.
 *
 *  `onProgress` is fired for every server event AND for client-side embedding
 *  progress (we synthesize embedding events as `{type:"progress",step:"embed",...}`).
 */
/** Parse a file server-side via `/parse/stream` and return the structured
 *  result (chunks as markdown). Shared by `parseAndEmbed` (which then embeds for
 *  the index) and `parseFileToMarkdown` (inline chat attachments — no embedding). */
export async function parseFileStream(
  file: File,
  cfg: Config,
  onProgress?: (event: ParseProgress) => void,
  signal?: AbortSignal,
): Promise<ParsedDocResponse> {
  const form = new FormData();
  form.append("file", file);

  const res = await authFetch(`${baseUrl(cfg)}/parse/stream`, {
    method: "POST",
    body: form,
    signal,
  });
  if (!res.ok) await throwIfBad(res);
  if (!res.body) {
    throw new ServerError(500, tr({ fr: "Serveur: pas de corps de réponse", en: "Server: empty response body" }));
  }

  // Parse SSE stream. Format: lines `data: <json>\n\n`.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let parsed: ParsedDocResponse | null = null;
  let errorDetail: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const raw of events) {
      const line = raw.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      let evt: ParseProgress;
      try {
        evt = JSON.parse(payload) as ParseProgress;
      } catch {
        continue;
      }
      onProgress?.(evt);
      if (evt.type === "result") parsed = evt.data;
      else if (evt.type === "error") errorDetail = evt.detail;
    }
  }

  if (errorDetail) throw new ServerError(500, `${tr({ fr: "Serveur", en: "Server" })}: ${errorDetail}`);
  if (!parsed) throw new ServerError(500, tr({ fr: "Serveur: stream terminé sans résultat", en: "Server: stream ended without a result" }));
  return parsed;
}

/** Same chars/4 heuristic used by the chunker and the docs tab. Ballpark only. */
function estimateTokens(text: string): number {
  return Math.round(text.length / 4);
}

/** Parse a file to its full markdown for inline chat use — same extraction path
 *  as the index, but NO embedding (inline docs don't go into IndexedDB). Returns
 *  the joined markdown of all chunks plus a token estimate. */
export async function parseFileToMarkdown(
  file: File,
  cfg: Config,
  onProgress?: (event: ParseProgress) => void,
  signal?: AbortSignal,
): Promise<{ name: string; markdown: string; tokens: number; ocrUsed: boolean }> {
  const parsed = await parseFileStream(file, cfg, onProgress, signal);
  const markdown = parsed.chunks.map((c) => c.text).join("\n\n");
  return { name: parsed.name, markdown, tokens: estimateTokens(markdown), ocrUsed: parsed.ocrUsed };
}

export async function parseAndEmbed(
  file: File,
  cfg: Config,
  onProgress?: (event: ParseProgress | { type: "progress"; step: "embed"; done: number; total: number }) => void,
  signal?: AbortSignal,
): Promise<StoredDocument> {
  const parsed = await parseFileStream(file, cfg, onProgress, signal);

  // Step 2: client embeds the chunks via RCP. We surface per-batch progress to
  // the same callback so the UI keeps moving during long embedding runs.
  const total = parsed.chunks.length;
  const vectors = await embedTexts(
    parsed.chunks.map((c) => c.text),
    cfg,
    signal,
    (done) => onProgress?.({ type: "progress", step: "embed", done, total }),
  );

  return {
    id: parsed.docId,
    name: parsed.name,
    hash: parsed.hash,
    uploadedAt: Date.now(),
    nChunks: parsed.nChunks,
    parsedBy: parsed.parsedBy,
    ocrUsed: parsed.ocrUsed,
    chunks: parsed.chunks.map((c, i) => ({
      id: c.id,
      text: c.text,
      page: c.page,
      section: c.section,
      embedding: vectors[i],
    })),
  };
}
